import { useState, useEffect, useMemo } from 'react';
import { Grid, Typography, Box, Button, List, Checkbox, Tabs } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import showToast from 'components/Toast/Toast';
import Modal from 'components/Modal/Modal';
import ButtonCustom from 'components/ButtonCustom/ButtonCustom';
import * as S from './style';
import { RiFoldersFill, RiFileEditFill, RiQuestionFill, RiCheckboxFill } from "react-icons/ri";
import { FaCheckSquare } from "react-icons/fa";
import QuickIdeasIcon from 'assets/icons/quick-ideas.svg';
import refreshIcon from 'assets/icons/refresh.svg'
import { getCommunityFullCalendar, deleteCalendar, editWeekPost } from 'utils/requests/communities';
import { useParams } from 'react-router-dom';
import NewPostForm from '../forms/NewPostForm';

const ContentCalendar = ({ onEditCalendar, refetch }) => {
  const { communityId } = useParams();

  const [calendarData, setCalendarData] = useState({});
  const [weeksData, setWeeksData] = useState([]);
  const [postsData, setPostsData] = useState({});
  const [openConfirmDeletionModal, setOpenConfirmDeletionModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [templateActiveTab, setTemplateActiveTab] = useState(0);
  const [openReprogramModal, setOpenReprogramModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  const fetchCalendarData = async () => {
    try {
      const calendarData = await getCommunityFullCalendar(communityId);

      if (calendarData && calendarData.calendars && calendarData.calendars.length > 0) {
        const calendar = calendarData.calendars[0];
        setCalendarData(calendarData);

        if (calendar.weeks && calendar.weeks.length > 0) {
          setWeeksData(calendar.weeks);

          const postsMap = {};
          calendar.weeks.forEach((week) => {
            if (week.posts && week.posts.length > 0) {
              postsMap[week.id] = week.posts;
            }
          });
          setPostsData(postsMap);
        }
      } else {
        setCalendarData({});
        setWeeksData([]);
        setPostsData({});
      }
    } catch (error) {
      if (error.status !== 404) {
        showToast("Falha ao buscar dados do calendário.", "error");
      }
      setCalendarData({});
      setWeeksData([]);
      setPostsData({});
    }
  }

  useEffect(() => {
    fetchCalendarData();
  }, [communityId]); 

  const weeks = useMemo(() => {
    if (!weeksData || weeksData.length === 0) {
      return null;
    }
  
    try {
      const weeks = weeksData.map((week, index) => {
        const weekPosts = postsData[week.id] || [];
        
        const weekData = typeof week.week_data === 'string' ? JSON.parse(week.week_data) : week.week_data;
        
        return {
          title: `Semana ${index + 1}` ?? "",
          weekKey: `week${index + 1}` ?? "",
          weekId: week.id ?? "",
          contents: weekPosts.map((post, postIndex) => {
            const scriptData = typeof post.script_data === 'string' ? JSON.parse(post.script_data) : post.script_data;
            
            return {
              id: post.id ?? "",
              date: "",
              title: `Post ${postIndex + 1} - ${post.post_title}` ?? "",
              description: scriptData.description ?? "",
              format: scriptData.format ?? "",
              objective: scriptData.objective ?? "",
              tone: scriptData.tone ?? "",
              completed: scriptData.completed ?? false,
              originalIndex: postIndex,
              templates: scriptData.templates ?? ""
            };
          }),
          highlight: weekData.highlight ?? "",
          engagement: weekData.engagementSignal ?? "",
        };
      });
      
      return weeks;
    } catch (error) {
      console.error("Erro ao processar dados do calendário:", error);
      return null;
    }
  }, [weeksData, postsData]);

  const renderTemplateModalContent = (week) => {
    if (!week.contents || week.contents.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Não há conteúdos disponíveis para esta semana.
          </Typography>
        </Box>
      );
    }

    const postsWithTemplates = week.contents.filter(content => {
      return content.templates && (
        (content.templates.captions && content.templates.captions.length > 0) ||
        (content.templates.visualSuggestions && content.templates.visualSuggestions.length > 0) ||
        (content.templates.interactiveQuestions && content.templates.interactiveQuestions.length > 0) ||
        (content.templates.engagementChecklist && content.templates.engagementChecklist.length > 0) ||
        (content.templates.quickIdeas && content.templates.quickIdeas.length > 0)
      );
    });
    
    if (postsWithTemplates.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Não há templates disponíveis para esta semana.
          </Typography>
        </Box>
      );
    }

    const allCaptions = [];
    const allVisualSuggestions = [];
    const allInteractiveQuestions = [];
    const allEngagementChecklist = [];
    const allQuickIdeas = [];

    postsWithTemplates.forEach(post => {
      if (post.templates.captions) {
        post.templates.captions.forEach(caption => {
          allCaptions.push({ text: caption, postTitle: post.title });
        });
      }
      if (post.templates.visualSuggestions) {
        post.templates.visualSuggestions.forEach(suggestion => {
          allVisualSuggestions.push({ text: suggestion, postTitle: post.title });
        });
      }
      if (post.templates.interactiveQuestions) {
        post.templates.interactiveQuestions.forEach(question => {
          allInteractiveQuestions.push({ text: question, postTitle: post.title });
        });
      }
      if (post.templates.engagementChecklist) {
        post.templates.engagementChecklist.forEach(item => {
          allEngagementChecklist.push({ text: item, postTitle: post.title });
        });
      }
      if (post.templates.quickIdeas) {
        post.templates.quickIdeas.forEach(idea => {
          allQuickIdeas.push({ text: idea, postTitle: post.title });
        });
      }
    });

    const template = {
      captions: allCaptions,
      visualSuggestions: allVisualSuggestions,
      interactiveQuestions: allInteractiveQuestions,
      engagementChecklist: allEngagementChecklist,
      quickIdeas: allQuickIdeas
    };
    
    const getIconColor = (index, originalColor) => {
      return templateActiveTab === index ? "#FFFFFF" : originalColor;
    };
    
    const templateTabs = [
      { 
        label: 'Sugestão de Legendas', 
        content: template.captions || [], 
        icon: <RiFoldersFill color={getIconColor(0, "#FFD700")} size="20px" />,
        originalColor: "#FFD700"
      },
      { 
        label: 'Dicas Visuais', 
        content: template.visualSuggestions || [], 
        icon: <RiFileEditFill color={getIconColor(1, "#33CCFF")} size="20px" />,
        originalColor: "#33CCFF"
      },
      { 
        label: 'Perguntas', 
        content: template.interactiveQuestions || [], 
        icon: <RiQuestionFill color={getIconColor(2, "#CA0073")} size="20px" />,
        originalColor: "#CA0073"
      },
      { 
        label: 'Checklist de Engajamento', 
        content: template.engagementChecklist || [], 
        icon: <RiCheckboxFill color={getIconColor(3, "#33CCFF")} size="20px" />,
        originalColor: "#33CCFF"
      }
    ];

    return (
      <Grid container flexDirection="column" gap="20px">
        <Typography variant="h5" sx={{ fontSize: "24px", color: "#FFFFFF" }}>
          Template da {week.title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={templateActiveTab}
            onChange={(event, newValue) => setTemplateActiveTab(newValue)}
            sx={{ 
              background: "transparent", 
              display: "flex", 
              justifyContent: "center",
              '& .MuiTab-root': {
                color: 'white',
                borderRadius: '5px',
                margin: '0 4px',
                minHeight: '40px',
                textTransform: 'none',
                fontSize: '12px',
                padding: '8px 16px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(252, 101, 8, 0.1)',
                }
              },
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: '#FC6508',
                color: 'white',
              }
            }}
            TabIndicatorProps={{
              style: {
                display: 'none'
              }
            }}
          >
            {templateTabs.map((tab, index) => (
              <S.ListFilterTab
                key={index}
                label={tab.label}
                value={index}
                icon={tab.icon}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ borderRadius: '8px', p: 3, justifyContent: 'center' }}>
          {templateTabs[templateActiveTab] && (
            <>
              <List>
                {templateTabs[templateActiveTab].content.map((item, index) => (
                  <S.TemplateListItem key={index}>
                    <S.TemplateListItemBullet />
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <S.TemplateListItemText>{item.text}</S.TemplateListItemText>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#8F8F8F', 
                          fontSize: '11px', 
                          fontStyle: 'italic',
                          mt: '2px'
                        }}
                      >
                        {item.postTitle}
                      </Typography>
                    </Box>
                  </S.TemplateListItem>
                ))}
              </List>
            </>
          )}
          <Box sx={{ mt: '40px', border: '1px solid rgba(255, 255, 255, 0.55)', padding: '20px', borderRadius: '5px' }}>
            <Box display='flex' alignItems='center' gap='10px' mb='10px'>
              <img src={QuickIdeasIcon} alt="Ideias rápidas" style={{ width: '20px', height: '20px' }} />
              <Typography variant="subtitle1" sx={{ color: "#FC6009", fontWeight: "bold" }}>
                 Ideias rápidas
              </Typography> 
            </Box>
            <List>
              {(template.quickIdeas || []).map((item, index) => (
                <S.TemplateListItem key={index}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <S.TemplateListItemText className='quickIdeas'>{item.text}</S.TemplateListItemText>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#8F8F8F', 
                        fontSize: '11px', 
                        fontStyle: 'italic',
                        mt: '2px'
                      }}
                    >
                      {item.postTitle}
                    </Typography>
                  </Box>
                </S.TemplateListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Grid>
    );
  };

  const selectedWeekData = selectedWeek ? weeks?.find(week => week.weekKey === `week${selectedWeek}`) : null;

  const handleViewTemplates = (weekNumber) => {
    setSelectedWeek(weekNumber);
    setOpenTemplateModal(true);
    setTemplateActiveTab(0);
  };

  const handleCloseTemplateModal = () => {
    setOpenTemplateModal(false);
    setSelectedWeek(null);
    setTemplateActiveTab(0);
  };

  const handleDeleteCalendar = async () => {
    try {
      await deleteCalendar(calendarData?.calendars?.[0]?.id || calendarData?.[0]?.id)
      showToast("Calendário deletado com sucesso!", "success");
      setOpenConfirmDeletionModal(false);
      refetch();
    } catch (error) {
      showToast("Falha ao deletar calendário", "error");
    }
  };

  const handleOpenReprogramModal = (content, week) => {
    setSelectedContent({ ...content, week });
    setOpenReprogramModal(true);
  };

  const handleCloseReprogramModal = () => {
    setOpenReprogramModal(false);
    setSelectedContent(null);
  };

  const handleReprogramSuccess = (newPost, selectedContent) => {
    setPostsData(prevPostsData => {
      const updatedPostsData = { ...prevPostsData };
      if (updatedPostsData[selectedContent.week.weekId]) {
        updatedPostsData[selectedContent.week.weekId] = updatedPostsData[selectedContent.week.weekId].map(post => {
          if (post.id === selectedContent.id) {
            return {
              ...post,
              post_title: newPost.title,
              script_data: JSON.stringify({
                title: newPost.title,
                description: newPost.description,
                format: newPost.format,
                objective: newPost.objective,
                tone: newPost.tone,
                completed: false,
                templates: newPost.templates
              })
            };
          }
          return post;
        });
      }
      return updatedPostsData;
    });
  };

  return (
    <S.CalendarContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Grid container justifyContent='space-between'>
          <S.FormTitleGrid container flexDirection='column' item md={7}>
            <p>Calendário</p>
            <p className='subTitle'>Plano de conteúdo para 30 dias</p>
          </S.FormTitleGrid>
          <Grid display='flex' justifyContent='flex-end' alignItems='flex-end' item md={5} gap='10px'>
            <S.EditButton onClick={onEditCalendar}>
              <img src={refreshIcon} alt="Mudar calendário" />
              Mudar calendário
            </S.EditButton>
          </Grid>
        </Grid>
      </Box>
        {weeks && weeks.map((week, index) => {
          const allPostsCompleted = week.contents.length > 0 && week.contents.every(content => content.completed);
          
          return (
            <S.CheckboxAccordion sx={{ backgroundColor: "transparent !important", mt: '20px' }} key={week.weekKey}>
              <S.CheckboxAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{ color: "#fff" }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: "#FFFFFF" }}>
                    {week.title}
                  </Typography>
                  {allPostsCompleted && (
                    <FaCheckSquare  
                      style={{ 
                        color: '#4CAF50',
                        fontSize: '24px',
                        ml: 1
                      }} 
                    />
                  )}
                </Box>
              </S.CheckboxAccordionSummary>
            <S.CheckboxAccordionDetails sx={{ color: "#fff" }}>
              <Grid container flexDirection="column" px="30px" gap="20px">
                <Box container gap="20px" display="flex" flexDirection="row" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Grid container flexDirection="column" sx={{ width: '70%' }}>
                    <Typography className="subTitle" sx={{ color: '#8F8F8F' }}>
                      Destaque da semana
                    </Typography>
                    <Typography className="content">
                      {week.highlight}
                    </Typography>
                  </Grid>
                  <Grid container flexDirection="column" sx={{ width: '30%' }}>
                    <Typography className="subTitle" sx={{ color: '#8F8F8F' }}>
                      Sinal de engajamento
                    </Typography>
                    <Typography className="content">
                      {week.engagement}
                    </Typography>
                  </Grid>
                </Box>
                <Grid container flexDirection="column">
                  {week.contents.map((content) => {
                    const isCompleted = content.completed || false;
                    
                    const handleContentCompletion = async (contentId, newCompletedStatus, weekId) => {
                      try {
                        const originalPost = postsData[weekId]?.find(post => post.id === contentId);
                        if (!originalPost) {
                          throw new Error('Post não encontrado');
                        }
                        
                        const currentScriptData = typeof originalPost.script_data === 'string' 
                          ? JSON.parse(originalPost.script_data) 
                          : originalPost.script_data;
                        
                        const updatedScriptData = {
                          ...currentScriptData,
                          completed: newCompletedStatus
                        };
                        
                        await editWeekPost(contentId, {
                          script_data: JSON.stringify(updatedScriptData)
                        });
                        
                        setPostsData(prevPostsData => {
                          const updatedPostsData = { ...prevPostsData };
                          if (updatedPostsData[weekId] && Array.isArray(updatedPostsData[weekId])) {
                            updatedPostsData[weekId] = updatedPostsData[weekId].map(post => {
                              if (post.id === contentId) {
                                return {
                                  ...post,
                                  script_data: JSON.stringify(updatedScriptData)
                                };
                              }
                              return post;
                            });
                          }
                          return updatedPostsData;
                        });
                        
                        showToast(
                          newCompletedStatus 
                            ? "Conteúdo marcado como concluído!" 
                            : "Conteúdo marcado como não concluído!",
                          "success"
                        );
                      } catch (error) {
                        console.error("Erro ao atualizar status:", error);
                        showToast("Erro ao atualizar status", "error");
                      }
                    };
                    
                    return (
                      <Box key={content.id} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, flexDirection: 'column' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <Box display="flex" alignItems="center">
                            <Checkbox
                              checked={isCompleted}
                              onChange={(e) => handleContentCompletion(content.id, e.target.checked, week.weekId)}
                              sx={{
                                color: '#FC6508',
                                '&.Mui-checked': {
                                  color: '#FC6508',
                                },
                                p: 0,
                                mr: 1,
                              }}
                            />
                            <Typography sx={{ color: '#8F8F8F', fontSize: '16px' }}>
                              {content.title}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenReprogramModal(content, week)}
                            sx={{
                              color: '#FC6508',
                              borderColor: '#FC6508',
                              fontSize: '12px',
                              borderRadius: '5px',
                              px: 2,
                              py: 0.5,
                              '&:hover': {
                                backgroundColor: 'rgba(252, 101, 8, 0.1)',
                                borderColor: '#FC6508',
                              }
                            }}
                          >
                            Mudar Post
                          </Button>
                        </Box>
                        <Box>
                          <Typography className="content" sx={{ fontSize: '14px', textDecoration: isCompleted ? 'line-through' : 'none' }}>
                            {content.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Grid>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <S.EditButton onClick={() => handleViewTemplates(index + 1)} sx={{ width: '100% !important' }}>
                    Ver templates dessa semana
                  </S.EditButton>
                </Box>
              </Grid>
            </S.CheckboxAccordionDetails>
          </S.CheckboxAccordion>
        )})}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          mt: 3,
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            variant="contained"
            onClick={() => setOpenConfirmDeletionModal(true)}
            sx={{ 
              backgroundColor: '#E91E63',
              color: '#FFFFFF',
              borderRadius: '5px',
              px: 3,
              py: 1.5,
              fontSize: '14px',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C2185B',
              }
            }}
          >
            Apagar calendário
          </Button>
        </Box>
      </Box>
      <Modal size="lg" open={openTemplateModal} onClose={handleCloseTemplateModal}>
        {selectedWeekData && renderTemplateModalContent(selectedWeekData)}
      </Modal>
      <Modal size="sm" open={openConfirmDeletionModal} onClose={() => setOpenConfirmDeletionModal(false)}>
        <Grid container flexDirection="column" gap="20px">
          <p style={{ fontSize: "24px" }}>Confirmar exclusão</p>
          <p style={{ fontSize: "18px" }}>
            Deseja excluir esse calendário? Essa ação é irreversível e também irá apagar os roteiros gerados.
          </p>
          <Grid container gap="15px" justifyContent="center">
            <Grid item lg={3}>
              <ButtonCustom
                label="Cancelar"
                onClick={() => setOpenConfirmDeletionModal(false)}
                sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
              ></ButtonCustom>
            </Grid>
            <Grid item lg={3}>
              <ButtonCustom label="Confirmar" onClick={handleDeleteCalendar}></ButtonCustom>
            </Grid>
          </Grid>
        </Grid>
      </Modal>
      <NewPostForm
        selectedContent={selectedContent}
        onClose={handleCloseReprogramModal}
        onSuccess={handleReprogramSuccess}
        communityId={communityId}
        openModal={openReprogramModal}
      />
    </S.CalendarContainer>
  );
};

export default ContentCalendar;
